import { collection, doc, addDoc, updateDoc, deleteDoc, getDoc, getDocs, query, where, serverTimestamp } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { EstadiaClienteCreateSchema, EstadiaClienteUpdateSchema } from '../models/estadiaCliente.schemas';
import type {
  EstadiaCliente,
  EstadiaClienteCreate,
  EstadiaClienteUpdate,
  StatusEstadiaCliente,
} from '../models/estadiaCliente.types';

/**
 * Operações Firestore para si_estadias_cliente — exclusivo do SI (ver firestore.rules,
 * mesmo padrão de si_equipamentos). Fase 1 do fluxo de máquina homologada por Cliente:
 * preparação/estoque ANTES de existir qualquer Solicitação (ver estadiaCliente.types.ts).
 */
class EstadiaClienteServiceClass {
  private collectionName = 'si_estadias_cliente';

  async create(data: EstadiaClienteCreate): Promise<string> {
    const validated = EstadiaClienteCreateSchema.parse(data);

    const docRef = await addDoc(collection(db, this.collectionName), {
      ...validated,
      status: validated.status ?? 'em_transito',
      chamados: validated.chamados ?? [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return docRef.id;
  }

  async update(data: EstadiaClienteUpdate): Promise<void> {
    const validated = EstadiaClienteUpdateSchema.parse(data);
    const { id, ...fields } = validated;

    await updateDoc(doc(db, this.collectionName, id), {
      ...fields,
      updatedAt: serverTimestamp(),
    });
  }

  /** Diferente de si_equipamentos: aqui é um registro de rastreamento, não a entidade em
   * si — apagar um cadastrado errado/duplicado não perde histórico de verdade (ver
   * firestore.rules). Se a estadia estava ativa, o cascade de
   * onEstadiaClienteWriteSyncEquipamentoCustody reseta a custódia do Equipamento. */
  async delete(id: string): Promise<void> {
    await deleteDoc(doc(db, this.collectionName, id));
  }

  async getById(id: string): Promise<EstadiaCliente | null> {
    const snap = await getDoc(doc(db, this.collectionName, id));
    if (!snap.exists()) {
      return null;
    }
    return { id: snap.id, ...snap.data() } as EstadiaCliente;
  }

  /** Lista para a tela "Estadias em Cliente" — filtros opcionais por cliente e status. */
  async listar(filtro?: { clienteId?: string; status?: StatusEstadiaCliente }): Promise<EstadiaCliente[]> {
    const restricoes = [
      ...(filtro?.clienteId ? [where('clienteId', '==', filtro.clienteId)] : []),
      ...(filtro?.status ? [where('status', '==', filtro.status)] : []),
    ];
    const snap = await getDocs(query(collection(db, this.collectionName), ...restricoes));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as EstadiaCliente);
  }

  /** Estadias já prontas (Fase 2 — sugestão de equipamento no envio, ver
   * SugestaoEstadiaClienteSection) pra um Cliente específico. */
  async listarProntasNoCliente(clienteId: string): Promise<EstadiaCliente[]> {
    return this.listar({ clienteId, status: 'pronta_aguardando_destino' });
  }

  /** Histórico de passagens por Cliente de um Equipamento específico (mais recente
   * primeiro) — resumo só-leitura mostrado na Movimentação, ver
   * HistoricoEstadiasEquipamentoSection. */
  async listarPorEquipamento(equipamentoId: string): Promise<EstadiaCliente[]> {
    const snap = await getDocs(query(collection(db, this.collectionName), where('equipamentoId', '==', equipamentoId)));
    const estadias = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as EstadiaCliente);
    return estadias.sort((a, b) => (b.createdAt?.toMillis() ?? 0) - (a.createdAt?.toMillis() ?? 0));
  }
}

export const estadiaClienteService = new EstadiaClienteServiceClass();
